import React, { useState } from "react";

import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { Menu, MenuItemType } from "@samskip/frontend-components";
import { navigate } from "gatsby";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { NavigationConstants } from "@assets/constants";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import { DeleteItemModal, SwitchCompanyModal } from "@components";
import BookingTemplateService from "@services/BookingTemplateService";
import {
  getTemplates,
  getTemplatePartnersFilterValues,
} from "@state/slices/bookingTemplateSlice";
import {
  switchCompany,
  userProfileState,
} from "@state/slices/userProfileSlice";

enum ActionTypes {
  EDIT = 1,
  COPY,
  DELETE,
}

type BookingTemplateActionMenuProps = {
  bookingTemplateId: number;
  bookingTemplateName: string;
  bookingTemplatePartner: string;
};

const BookingTemplateActionMenu = ({
  bookingTemplateId,
  bookingTemplateName,
  bookingTemplatePartner,
}: BookingTemplateActionMenuProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionTypes | null>(
    null
  );
  const { t } = useTranslation();

  const { userProfile, isSwitchingCompanies } =
    useAppSelector(userProfileState);
  const dispatch = useAppDispatch();

  const isTemplateFromSelectedCompany =
    userProfile?.SelectedCompany.PartnerCode !== bookingTemplatePartner;

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const closeSwitchCompanyModal = () => {
    setSelectedAction(null);
  };

  const handleMenuItemActions = (actionType: ActionTypes | null) => {
    switch (actionType) {
      case ActionTypes.EDIT:
        navigate(
          `${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}/edit/${bookingTemplateId}`
        );
        break;
      case ActionTypes.COPY:
        navigate(
          `${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}/copy/${bookingTemplateId}`
        );
        break;
      case ActionTypes.DELETE:
        closeSwitchCompanyModal();
        openDeleteModal();
        break;
      default:
        break;
    }
  };

  const handleSwitchCompany = () => {
    dispatch(switchCompany(bookingTemplatePartner)).then(() => {
      handleMenuItemActions(selectedAction);
    });
  };

  const handleMenuItemClick = (actionType: ActionTypes) => {
    if (isTemplateFromSelectedCompany) {
      setSelectedAction(actionType);
    } else {
      handleMenuItemActions(actionType);
    }
  };

  const handleDeleteTemplate = () => {
    BookingTemplateService.disableTemplate(bookingTemplateId)
      .then(() => {
        toast.success(t("LABEL_TEMPLATE_DELETE_SUCCESS"));
        dispatch(getTemplates());
        dispatch(getTemplatePartnersFilterValues());
      })
      .catch(() => {
        toast.error(t("ERROR_TEMPLATE_DELETE_FAILED"));
      });
    closeDeleteModal();
  };

  const menuItems: MenuItemType[] = [
    {
      id: "edit-template-action",
      onClick: () => handleMenuItemClick(ActionTypes.EDIT),
      label: t("LABEL_EDIT_TEMPLATE"),
    },
    {
      id: "copy-template-action",
      onClick: () => handleMenuItemClick(ActionTypes.COPY),
      label: t("LABEL_COPY_TEMPLATE"),
    },
    {
      id: "delete-template-action",
      onClick: () => handleMenuItemClick(ActionTypes.DELETE),
      label: t("LABEL_DELETE_TEMPLATE"),
    },
  ];

  return (
    <div className="relative">
      <Menu
        items={menuItems}
        size="small"
        triggerProps={{ icon: faEllipsis }}
      />
      {!!isDeleteModalOpen && (
        <DeleteItemModal
          content={`${t("TEXT_DELETE_TEMPLATE_N", {
            n: bookingTemplateName,
          })}`}
          handleDelete={handleDeleteTemplate}
          onClose={closeDeleteModal}
          title={t("LABEL_DELETE_TEMPLATE")}
        />
      )}
      {!!selectedAction && (
        <SwitchCompanyModal
          company={bookingTemplatePartner}
          isLoading={isSwitchingCompanies}
          onClose={closeSwitchCompanyModal}
          onSwitchCompany={handleSwitchCompany}
        />
      )}
    </div>
  );
};

export default BookingTemplateActionMenu;
